import { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import axiosInstance from '../config/axios.config';

/**
 * Referral Link Dialog Component
 * Professional dialog for MLM network growth
 *
 * Features:
 * - Generates PERMANENT referral link (reuses existing)
 * - Copy to clipboard functionality
 * - WhatsApp & Email sharing
 * - No expiry - links work forever
 * - Shows usage statistics
 * - Professional UI with PrimeReact components
 */
const ReferralLinkDialog = ({ visible, onHide }) => {
    const [linkData, setLinkData] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);

    const toast = useRef(null);

    // Generate referral link when dialog opens
    useEffect(() => {
        if (visible) {
            generateReferralLink();
        } else {
            // Reset state when dialog closes
            setLinkData(null);
            setError(null);
        }
    }, [visible]);

    // Generate new referral link
    const generateReferralLink = async () => {
        setGenerating(true);
        setError(null);

        try {
            const response = await axiosInstance.post('/auth/generate-referral-link');

            if (response.data.success) {
                setLinkData({
                    url: response.data.link
                });

                toast.current?.show({
                    severity: 'success',
                    summary: 'Link Ready',
                    detail: 'Your permanent referral link is ready to share',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error generating referral link:', error);
            setError(error.response?.data?.message || 'Failed to generate referral link');

            toast.current?.show({
                severity: 'error',
                summary: 'Generation Failed',
                detail: error.response?.data?.message || 'Failed to generate referral link',
                life: 5000
            });
        } finally {
            setGenerating(false);
        }
    };

    // Copy link to clipboard
    const handleCopyLink = async () => {
        if (!linkData?.url) return;

        try {
            await navigator.clipboard.writeText(linkData.url);

            toast.current?.show({
                severity: 'success',
                summary: 'Copied!',
                detail: 'Referral link copied to clipboard',
                life: 2000
            });
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Copy Failed',
                detail: 'Failed to copy link. Please select and copy manually.',
                life: 3000
            });
        }
    };

    // Share via WhatsApp
    const handleWhatsAppShare = () => {
        if (!linkData?.url) return;

        const message = encodeURIComponent(
            `Join my network and unlock access to credit bureau reports, lead management, and grow your own network!\n\nRegister here: ${linkData.url}`
        );

        window.open(`https://wa.me/?text=${message}`, '_blank');

        toast.current?.show({
            severity: 'info',
            summary: 'Opening WhatsApp',
            detail: 'Share your referral link with your contact',
            life: 3000
        });
    };

    // Share via Email
    const handleEmailShare = () => {
        if (!linkData?.url) return;

        const subject = encodeURIComponent('Join My Network - Credit Bureau & Lead Management');
        const body = encodeURIComponent(
            `Hi,\n\nI'd like to invite you to join my network!\n\nWith this platform, you can:\n• Create and manage leads\n• Generate credit bureau reports\n• Build your own referral network\n• Track your network growth\n\nRegister using this link:\n${linkData.url}\n\nLooking forward to having you on board!`
        );

        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');

        toast.current?.show({
            severity: 'info',
            summary: 'Opening Email',
            detail: 'Share your referral link via email',
            life: 3000
        });
    };

    // Dialog footer
    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Close"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-secondary"
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-share-alt text-primary text-2xl"></i>
                        <span className="font-semibold">My Referral Link</span>
                    </div>
                }
                visible={visible}
                onHide={onHide}
                footer={dialogFooter}
                style={{ width: '600px' }}
                modal
                dismissableMask={false}
                className="referral-link-dialog"
            >
                {/* Loading State */}
                {generating && (
                    <div className="flex flex-column align-items-center justify-content-center p-5">
                        <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '3rem' }}></i>
                        <p className="mt-3 text-600">Generating your unique referral link...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !generating && (
                    <Message
                        severity="error"
                        text={error}
                        className="w-full mb-3"
                    />
                )}

                {/* Success State - Show Link */}
                {linkData && !generating && (
                    <div>
                        {/* Information Banner */}
                        <Message
                            severity="info"
                            className="w-full mb-4"
                            content={
                                <div className="flex align-items-start">
                                    <i className="pi pi-info-circle mr-2 mt-1"></i>
                                    <div>
                                        <p className="m-0 font-semibold">Grow Your Network</p>
                                        <p className="m-0 mt-1 text-sm">
                                            Share this permanent link with anyone. When they register, they'll join your MLM network.
                                            <strong> Unlimited people</strong> can use this link - it never expires!
                                        </p>
                                    </div>
                                </div>
                            }
                        />

                        {/* Link Display Section */}
                        <div className="mb-4">
                            <h6 className="text-900 mb-3">
                                <i className="pi pi-link mr-2"></i>
                                Your Referral Link
                            </h6>

                            <div className="p-inputgroup">
                                <InputText
                                    value={linkData.url}
                                    readOnly
                                    className="w-full"
                                    style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                />
                                <Button
                                    icon="pi pi-copy"
                                    aria-label="Copy referral link to clipboard"
                                    className="p-button-primary"
                                    onClick={handleCopyLink}
                                    tooltip="Copy to clipboard"
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>

                            <div className="mt-2 text-sm text-success-600">
                                <i className="pi pi-check-circle mr-1"></i>
                                Permanent link - Never expires
                            </div>
                        </div>

                        <Divider />

                        {/* Share Options Section */}
                        <div className="mb-4">
                            <h6 className="text-900 mb-3">
                                <i className="pi pi-send mr-2"></i>
                                Quick Share
                            </h6>

                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <Button
                                        label="Share on WhatsApp"
                                        icon="pi pi-whatsapp"
                                        onClick={handleWhatsAppShare}
                                        className="p-button-success w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-6">
                                    <Button
                                        label="Share via Email"
                                        icon="pi pi-envelope"
                                        onClick={handleEmailShare}
                                        className="p-button-info w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        <Divider />

                        {/* Benefits Section */}
                        <div className="surface-100 border-round p-3">
                            <h6 className="mt-0 mb-2 text-900">
                                <i className="pi pi-users mr-2"></i>
                                What Your Referrals Get
                            </h6>
                            <ul className="m-0 pl-3 text-600">
                                <li>Access to credit bureau reports</li>
                                <li>Lead management system</li>
                                <li>Ability to build their own network</li>
                                <li>Network growth tracking</li>
                            </ul>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default ReferralLinkDialog;
